import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Divider, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import MuiDialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { styled, useTheme } from '@mui/material/styles';

import { FunctionComponent } from '@sbv-clients/util-misc';

import { useWindowedDialog } from '../../hooks';
import { Icon } from '../icon';
import IconButton from '../icon-button/icon-button';
import { useDialogClose } from './dialog-close-context';
import { DialogCloseGuard } from './dialog-close-guard';
import {
  DialogWindowedControlsContext,
  useDialogWindowedControls,
} from './dialog-windowed-controls-context';
import { DragHandleClassContext, DraggablePaper } from './draggable';
import { WindowedTooltipZIndexContext } from './windowed-tooltip-zindex-context';

const DialogAmbientZIndexContext = createContext<number | undefined>(undefined);

const WINDOWED_DIALOG_SX = {
  pointerEvents: 'none',
  '& .MuiPaper-root': {
    pointerEvents: 'auto',
    position: 'fixed',
    top: 'auto',
    left: 'auto',
    right: 16,
    bottom: 16,
    margin: 0,
    width: 'clamp(380px, 26vw, 480px) !important',
    maxWidth: 'calc(100vw - 32px) !important',
    height: 'clamp(420px, 55vh, 640px) !important',
    maxHeight: 'calc(100vh - 32px) !important',
    minWidth: 'unset !important',
    minHeight: 'unset !important',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #dce1e8',
    transform: 'none !important',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.28)',
  },
} as const;

const SMALL_SCREEN_DIALOG_SX = {
  '& .MuiDialog-paper': {
    maxWidth: 'calc(100% - 32px) !important',
  },
} as const;

const WINDOWED_MIN_HEIGHT = 300;
const WINDOWED_MAX_HEIGHT_MARGIN = 32;

const WindowedResizeHandle = ({
  onMouseDown,
}: {
  onMouseDown: (event: React.MouseEvent) => void;
}) => (
  <Box
    onMouseDown={onMouseDown}
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '6px',
      zIndex: 1,
      cursor: 'ns-resize',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.06)' },
      '&::after': {
        content: '""',
        display: 'block',
        width: '40px',
        height: '3px',
        borderRadius: '2px',
        backgroundColor: '#dce1e8',
      },
    }}
  />
);

const Dialog = ({
  windowed: windowedProp,
  isMinimizeable,
  sx,
  children,
  open,
  ...props
}: DialogProps & { windowed?: boolean; isMinimizeable?: boolean }) => {
  const internalWindowedDialog = useWindowedDialog();
  const windowed = isMinimizeable
    ? internalWindowedDialog.windowed
    : windowedProp;

  const [customHeight, setCustomHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (isMinimizeable && open) {
      internalWindowedDialog.reset();
      setCustomHeight(undefined);
    }
  }, [open, isMinimizeable]);

  const sxArray = Array.isArray(sx) ? sx : sx ? [sx] : [];
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [paperElement, setPaperElement] = useState<HTMLDivElement | null>(null);
  const paperRefCallback = useCallback((node: HTMLDivElement | null) => {
    setPaperElement(node);
  }, []);

  const resizeDragState = useRef({ startY: 0, startHeight: 0 });
  const onResizeMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const startHeight =
        paperElement?.getBoundingClientRect().height ?? WINDOWED_MIN_HEIGHT;
      resizeDragState.current = { startY: event.clientY, startHeight };

      const onMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientY - resizeDragState.current.startY;
        const maxHeight = window.innerHeight - WINDOWED_MAX_HEIGHT_MARGIN;
        const nextHeight = Math.min(
          maxHeight,
          Math.max(
            WINDOWED_MIN_HEIGHT,
            resizeDragState.current.startHeight - delta
          )
        );
        setCustomHeight(nextHeight);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [paperElement]
  );

  const ambientZIndex = useContext(DialogAmbientZIndexContext);
  const dialogZIndex =
    windowed || ambientZIndex !== undefined
      ? Math.max(
          windowed ? theme.zIndex.tooltip + 1 : theme.zIndex.modal,
          (ambientZIndex ?? 0) + 1
        )
      : undefined;

  return (
    <MuiDialog
      PaperComponent={DraggablePaper}
      open={open}
      {...props}
      hideBackdrop={windowed || props.hideBackdrop}
      disableScrollLock={windowed || props.disableScrollLock}
      disableEnforceFocus={windowed || props.disableEnforceFocus}
      sx={[
        ...(windowed ? [WINDOWED_DIALOG_SX] : []),
        ...(isSmallScreen && !windowed && !props.fullScreen
          ? [SMALL_SCREEN_DIALOG_SX]
          : []),
        ...(windowed && customHeight !== undefined
          ? [{ '& .MuiPaper-root': { height: `${customHeight}px !important` } }]
          : []),
        ...(dialogZIndex !== undefined ? [{ zIndex: dialogZIndex }] : []),
        ...sxArray,
      ]}
      PaperProps={{ ...props.PaperProps, ref: paperRefCallback }}
    >
      {windowed && <WindowedResizeHandle onMouseDown={onResizeMouseDown} />}
      <WindowedTooltipZIndexContext.Provider
        value={
          dialogZIndex !== undefined
            ? {
                zIndex: dialogZIndex + 1,
                boundaryElement: windowed ? paperElement : null,
              }
            : undefined
        }
      >
        <DialogAmbientZIndexContext.Provider
          value={dialogZIndex ?? ambientZIndex}
        >
          <DialogWindowedControlsContext.Provider
            value={
              isMinimizeable
                ? {
                    windowed: internalWindowedDialog.windowed,
                    toggleWindowed: internalWindowedDialog.toggleWindowed,
                  }
                : undefined
            }
          >
            {children}
          </DialogWindowedControlsContext.Provider>
        </DialogAmbientZIndexContext.Provider>
      </WindowedTooltipZIndexContext.Provider>
    </MuiDialog>
  );
};

const DialogFormFlexWrapper: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      minHeight: 0,
      '& > form': {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
      },
    }}
  >
    {children}
  </Box>
);

const StyledDialogContent = styled(Box)`
  overflow-y: auto;
  max-height: 60vh;
  padding: ${(props) => `${props.theme.spacing(4)} ${props.theme.spacing(6)}`};
`;

const DialogHeader = ({
  title,
  subTitle,
  onClose,
  icon,
  windowed: windowedProp,
  onToggleWindowed: onToggleWindowedProp,
}: {
  title: string;
  subTitle?: string;
  onClose?: () => void;
  icon?: any;
  windowed?: boolean;
  onToggleWindowed?: () => void;
}) => {
  const dragHandleClass = useContext(DragHandleClassContext);
  const dialogClose = useDialogClose();
  const dialogControls = useDialogWindowedControls();
  const windowed = windowedProp ?? dialogControls?.windowed ?? false;
  const onToggleWindowed =
    onToggleWindowedProp ?? dialogControls?.toggleWindowed;
  const theme = useTheme();

  return (
    <DialogTitle
      id="draggable-dialog-title"
      className={windowed ? undefined : dragHandleClass}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        cursor: windowed ? 'default' : 'move',
        borderBottom: `1px solid ${theme.colors.secondary.lighter}`,
      }}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {icon}
          {title}
        </Box>
        {subTitle && (
          <Box
            style={{
              color: theme.text.secondary,
              fontWeight: 400,
              marginTop: 6,
            }}
          >
            {' ' + subTitle}
          </Box>
        )}
      </Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        {onToggleWindowed && (
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              onToggleWindowed();
            }}
            sx={{ border: '1px solid #DCE1E8' }}
          >
            <Icon
              sx={!windowed ? { transform: 'scaleY(-1)' } : undefined}
              name={windowed ? 'OpenInFull' : 'CollapseSVG'}
              iconProps={
                windowed
                  ? { fontSize: 'small', sx: { fontSize: 18 } }
                  : { width: 18, height: 18 }
              }
            />
          </IconButton>
        )}
        {onClose && (
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              (dialogClose ?? onClose)();
            }}
            sx={{ border: `1px solid ${theme.colors.secondary.lighter}` }}
          >
            <Icon
              name={'Close'}
              iconProps={{
                color: 'error',
                fontSize: 'small',
                sx: { fontSize: 18 },
              }}
            />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  );
};

const StyledDivider = styled(Divider)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.palette.text.primary};
  width: 100%;
`;

const DialogActionsContainer: FunctionComponent = ({ children }) => {
  return (
    <>
      <StyledDivider />
      <DialogActions sx={{ p: 4, gap: 2 }}>{children}</DialogActions>
    </>
  );
};

export {
  Dialog,
  DialogFormFlexWrapper,
  DialogContentText,
  DialogActions,
  DialogContent,
  StyledDialogContent,
  DialogTitle,
  DialogHeader,
  DialogActionsContainer,
  DialogCloseGuard,
  useDialogClose,
};
